import { useMemo, useState } from 'react'
import { Alert, Card, Col, DatePicker, Row, Segmented, Space, Statistic, Table, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { RiseOutlined } from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import { type Dayjs } from 'dayjs'
import { useTranslation } from 'react-i18next'
import { translateError } from '@/shared/api/errors'
import { queryKeys } from '@/shared/api/queryKeys'
import { usePermissions } from '@/shared/auth/usePermissions'
import {
  downloadReportExport,
  fetchSubscriberMovement,
  type Granularity,
  type SubscriberMovementRow,
} from '@/features/reports/reportsApi'
import { ReportFilterBar } from '@/features/finance/ReportFilterBar'
import { ReportTableEmpty } from '@/features/finance/ReportTableEmpty'
import { matchesSearch, useLocalListState } from '@/shared/lib/localList'
import { defaultDayRange } from '@/shared/lib/dayRange'
import { formatNumber } from '@/shared/lib/format'
import { PageHeader } from '@/shared/ui/PageHeader'
import { ServerExportButton } from '@/shared/ui/ServerExportButton'

const { RangePicker } = DatePicker
const ISO_DAY = 'YYYY-MM-DD'

/** Jadval tartibi — URL'da `f.order` bo'lib saqlanadi */
const ORDER_OLDEST = 'OLDEST'

/**
 * Abonent bazasining harakati — `GET /api/reports/subscriber-movement`.
 *
 * <p>Mijoz hujjatining §5.5 bandlari «abonent bazasi o'sishi» va
 * «ketish darajasi (churn)». Ular alohida hisobot EMAS: ikkalasi ham
 * `subscriber.subscriber` ning AYNI ikki ustunidan (`created_at`,
 * `closed_at`) chiqadi va faqat ko'rsatish shakli bilan farq qiladi.
 *
 * <p>⚠️ Bu ma'lumot panelda ilgari FAQAT EKSPORT sifatida ochilardi.
 *
 * <h2>⚠️ CHURN NOL EMAS, «—» BO'LISHI MUMKIN</h2>
 * <p>Maxraj — oyna BOSHIDAGI baza. U nol bo'lsa server `null` yuboradi
 * va panel chiziqcha chizadi: «churn 0%, hammasi joyida» degan MA'NOLI
 * javob berish yolg'on bo'lardi, holbuki savol umuman qo'yilmagan.
 * O'lchangan (Staging, 2026-08-14): 15-iyul .. 14-avgust oynasida
 * `openingBase = 0`, ya'ni churn maydoni javobda umuman kelmaydi.
 *
 * <h2>⚠️ TENGLIK EKRANDA KO'RSATILADI</h2>
 * <p>`openingBase + joined − closed = closingBase`. Panel bu sonlarni
 * QAYTA HISOBLAMAYDI — u serverdan kelganini ko'rsatadi va tenglik
 * buzilsa OCHIQ aytadi. Buzilish serverdagi kun chegarasi yoki doira
 * xatosi bo'ladi va uni yashirish nuqsonni ko'rinmas qilardi.
 *
 * <h2>⚠️ MANBANING MA'LUM CHEKLOVI</h2>
 * <p>Yopilgan abonent qayta ochilsa `closed_at` tozalanadi, ya'ni
 * uning ketgani tarixdan o'chadi va churn KAM ko'rinadi. Bu
 * hisobotning nuqsoni emas (`SubscriberMovement` javadoc'i), lekin
 * ekranda yozib qo'yiladi: teskarisi («churn past, demak yaxshi»)
 * jimgina xato xulosa bo'lardi.
 *
 * <h2>⚠️ SAHIFALASH YO'Q — ENDPOINT UNI QO'LLAMAYDI</h2>
 * <p>`SubscriberReportResource#movement` faqat `from`/`to` va
 * `granularity` ni oladi: `page`, `size`, `sort` YO'Q. Qatorlar soni
 * oyna uzunligiga teng (kunlar yoki oylar), ya'ni u so'rovning O'ZIDA
 * chegaralangan — sana oralig'i bu yerda sahifalash vazifasini bajaradi.
 *
 * <h2>⚠️ «ENG YANGI BIRINCHI» — SHU YERDA HALOL BAJARILADI</h2>
 * <p>`periodStart` MONOTON va butun qator to'plami bitta javobda keladi
 * (sahifa yo'q), ya'ni tartibni ag'darish BUTUN ro'yxatga qo'llanadi —
 * «faqat joriy sahifa saralandi» tuzog'i bu yerda mavjud emas. Sukut
 * bo'yicha eng yangi davr birinchi; xronologik o'qish kerak bo'lganda
 * (o'sish tendensiyasi) tartib bir bosishda almashadi va tanlov
 * MANZILDA saqlanadi.
 */
export function SubscriberMovementPage() {
  const { t } = useTranslation()
  const { can } = usePermissions()
  const filter = useLocalListState()

  const [granularity, setGranularity] = useState<Granularity>('DAY')
  const [range, setRange] = useState<[Dayjs, Dayjs]>(() => defaultDayRange(29))

  const from = range[0].format(ISO_DAY)
  const to = range[1].format(ISO_DAY)

  const report = useQuery({
    queryKey: queryKeys.reports.subscriberMovement(from, to, granularity),
    queryFn: ({ signal }) => fetchSubscriberMovement({ from, to }, granularity, signal),
  })

  const data = report.data

  // ⚠️ TEKSHIRUV, hisob EMAS: server bergan to'rt sonning o'zaro
  // muvofiqligi. Panel hech qaysisini almashtirmaydi.
  const balanced =
    data === undefined || data.openingBase + data.joined - data.closed === data.closingBase

  const oldestFirst = filter.values['order']?.[0] === ORDER_OLDEST
  const rows = useMemo(() => data?.rows ?? [], [data])
  const visible = useMemo(() => {
    const matched = rows.filter((row) => matchesSearch(filter.search, row.label, row.periodStart))
    // ⚠️ Manba tartibiga TAYANMAYMIZ: server qatorlarni xronologik
    // beradi, lekin bu shartnomada yozilmagan. `periodStart` — ISO
    // satr, ya'ni matn solishtiruvi vaqt solishtiruviga TENG.
    return matched
      .slice()
      .sort((a, b) =>
        oldestFirst
          ? a.periodStart.localeCompare(b.periodStart)
          : b.periodStart.localeCompare(a.periodStart),
      )
  }, [rows, filter.search, oldestFirst])

  const columns: ColumnsType<SubscriberMovementRow> = [
    {
      key: 'label',
      title: t('reports.movement.columns.period'),
      render: (_, row) => row.label,
    },
    {
      key: 'joined',
      align: 'right',
      width: 150,
      title: t('reports.movement.columns.joined'),
      // ⚠️ `0` — O'LCHANGAN NOL: server har qatorda `joined` ni SON
      // sifatida beradi (`movementRowSchema`, ixtiyoriy emas). «Bu kuni
      // hech kim qo'shilmadi» — HAQIQIY javob, ma'lumot yo'qligi emas.
      render: (_, row) => formatNumber(row.joined),
    },
    {
      key: 'closed',
      align: 'right',
      width: 150,
      title: t('reports.movement.columns.closed'),
      render: (_, row) => formatNumber(row.closed),
    },
    {
      key: 'netChange',
      align: 'right',
      width: 150,
      title: t('reports.movement.columns.netChange'),
      render: (_, row) => (
        <Typography.Text type={row.netChange < 0 ? 'danger' : row.netChange > 0 ? 'success' : undefined}>
          {row.netChange > 0 ? '+' : ''}
          {formatNumber(row.netChange)}
        </Typography.Text>
      ),
    },
  ]

  return (
    <>
      <PageHeader
        title={t('reports.movement.title')}
        description={t('reports.movement.intro')}
        extra={
          can('report.export') ? (
            <ServerExportButton
              size="middle"
              title={t('reports.movement.title')}
              disabled={report.isError}
              estimatedRows={data?.rows.length ?? null}
              run={(format, onProgress) =>
                downloadReportExport('subscriber-movement', format, {
                  from,
                  to,
                  granularity,
                  onProgress,
                })
              }
            />
          ) : null
        }
      />

      <Card size="small" style={{ marginBottom: 16 }}>
        <Space wrap size="middle" align="center">
          <Typography.Text type="secondary">{t('reports.movement.rangeLabel')}:</Typography.Text>
          <RangePicker
            value={range}
            onChange={(value) => {
              if (value?.[0] && value[1]) setRange([value[0], value[1]])
            }}
            allowClear={false}
            format="DD.MM.YYYY"
          />
          {/* ⚠️ Yorliqlar `ReportsPage` bilan AYNI kalitlardan — bir
              tushuncha ikki xil nomlanmasin */}
          <Segmented<Granularity>
            value={granularity}
            onChange={setGranularity}
            options={[
              { value: 'DAY', label: t('reports.byDay') },
              { value: 'MONTH', label: t('reports.byMonth') },
            ]}
          />
        </Space>
      </Card>

      {data?.scopeRestricted ? (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('reports.movement.scopeRestricted')}
        />
      ) : null}

      {report.isError ? (
        <Alert
          type="error"
          showIcon
          message={t('dataTable.loadFailed')}
          description={translateError(report.error, t)}
        />
      ) : (
        <>
          <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            <Col xs={12} md={6}>
              <Card size="small" loading={report.isPending}>
                <Statistic
                  title={t('reports.movement.kpi.openingBase')}
                  value={formatNumber(data?.openingBase ?? 0)}
                />
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card size="small" loading={report.isPending}>
                <Statistic
                  title={t('reports.movement.kpi.joined')}
                  valueStyle={{ color: '#389e0d' }}
                  value={formatNumber(data?.joined ?? 0)}
                />
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card size="small" loading={report.isPending}>
                <Statistic
                  title={t('reports.movement.kpi.closed')}
                  valueStyle={{ color: '#cf1322' }}
                  value={formatNumber(data?.closed ?? 0)}
                />
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card size="small" loading={report.isPending}>
                <Statistic
                  title={t('reports.movement.kpi.closingBase')}
                  value={formatNumber(data?.closingBase ?? 0)}
                />
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card size="small" loading={report.isPending}>
                <Statistic
                  title={t('reports.movement.kpi.netChange')}
                  valueStyle={(data?.netChange ?? 0) < 0 ? { color: '#cf1322' } : undefined}
                  value={`${(data?.netChange ?? 0) > 0 ? '+' : ''}${formatNumber(data?.netChange ?? 0)}`}
                />
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card size="small" loading={report.isPending}>
                {/*
                  ⚠️ `null` → «—», NOL EMAS. Sabab sahifa javadoc'ida:
                  oyna boshida baza nol bo'lsa savol qo'yilmagan.
                */}
                <Statistic
                  title={t('reports.movement.kpi.churn')}
                  value={
                    data?.churnPercent === null || data?.churnPercent === undefined
                      ? '—'
                      : `${data.churnPercent.toFixed(2)}%`
                  }
                />
                {data && data.churnPercent === null ? (
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {t('reports.movement.churnUndefined')}
                  </Typography.Text>
                ) : null}
              </Card>
            </Col>
          </Row>

          {/*
            Tenglik buzilsa — OCHIQ xato. Panel sonni tuzatmaydi, chunki
            qaysi biri noto'g'ri ekanini u bilmaydi.
          */}
          {!balanced ? (
            <Alert
              type="error"
              showIcon
              style={{ marginBottom: 16 }}
              message={t('reports.movement.identityBroken')}
              description={t('reports.movement.identityBrokenHint')}
            />
          ) : null}

          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message={t('reports.movement.reopenLimit')}
          />

          <Card title={t('reports.movement.tableTitle')} size="small">
            <ReportFilterBar
              filter={filter}
              searchPlaceholder={t('reports.movement.searchPlaceholder')}
              shown={visible.length}
              loaded={rows.length}
              totalsNote={t('finance.reportTable.totalsUnfiltered')}
              extra={
                <Segmented<string>
                  value={oldestFirst ? ORDER_OLDEST : 'NEWEST'}
                  onChange={(value) =>
                    filter.setValues('order', value === ORDER_OLDEST ? [ORDER_OLDEST] : [])
                  }
                  options={[
                    { value: 'NEWEST', label: t('reports.movement.orderNewest') },
                    { value: ORDER_OLDEST, label: t('reports.movement.orderOldest') },
                  ]}
                />
              }
            />

            <Table<SubscriberMovementRow>
              rowKey="periodStart"
              columns={columns}
              dataSource={visible}
              loading={report.isPending}
              pagination={false}
              size="small"
              scroll={{ x: 700 }}
              locale={{
                emptyText: (
                  <ReportTableEmpty
                    filter={filter}
                    icon={<RiseOutlined />}
                    title={t('reports.movement.empty')}
                    description={t('reports.movement.emptyHint')}
                  />
                ),
              }}
            />
          </Card>
        </>
      )}
    </>
  )
}
